import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, FileDown } from "lucide-react";
import RegulatoryBadges from "@/components/RegulatoryBadges";

function IconFor({ name }) {
  const map = {
    "shield-check": Icons.ShieldCheck,
    "gavel": Icons.Gavel,
    "activity": Icons.Activity,
    "graduation-cap": Icons.GraduationCap,
    "sparkles": Icons.Sparkles,
    "building": Icons.Building2,
    "scale": Icons.Scale,
  };
  const Cmp = map[name] || Icons.Sparkles;
  return <Cmp size={30} className="text-gold" />;
}

export default function Services() {
  const { t, lang, pick } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setItems(
    (r.data || []).filter((s) => s.is_active !== false && s.is_archived !== true)
  )); }, []);

  return (
    <div data-testid="services-page">
      <section className="bg-navy text-cream py-24">
        <div className="container-x">
          <span className="eyebrow">{t("nav.services")}</span>
          <h1 className="mt-4 text-4xl md:text-6xl">
            {lang === "ar" ? "خدماتنا الاستشارية" : "Advisory Practices"}
          </h1>
          <span className="divider-gold" />
          <p className="text-cream/80 max-w-2xl leading-relaxed">
            {lang === "ar"
              ? "خدمات مصممة بمعايير مؤسسات النخبة، تجمع بين الأُطر التنظيمية العالمية والفهم الإقليمي العميق."
              : "Services crafted to elite standards, blending global frameworks with regional depth."}
          </p>
        </div>
      </section>

      {items.length === 0 ? (
        <section className="container-x py-24 text-center text-navy/50">
          {lang === "ar" ? "لا توجد خدمات منشورة حالياً." : "No services published yet."}
        </section>
      ) : (
        <section className="container-x py-24 grid grid-cols-1 md:grid-cols-2 gap-8">
          {items.map((s) => {
            const features = (lang === "ar" ? s.features_ar : s.features_en) || [];
            return (
              <div key={s.id || s.key} data-testid={`service-detail-${s.key}`} className="card-elev overflow-hidden hover:-translate-y-1 transition-transform">
                {s.image_url ? (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={s.image_url} alt={pick(s, "title")} className="w-full h-full object-cover" />
                  </div>
                ) : null}
                <div className="p-10">
                  <IconFor name={s.icon} />
                  <h2 className="mt-6 text-2xl md:text-3xl text-navy">{pick(s, "title")}</h2>
                  <RegulatoryBadges value={s.regulatory_frameworks} lang={lang} testId={`service-reg-${s.key || s.id}`} />
                  <span className="divider-gold" />
                  <p className="text-navy/75 leading-relaxed">{pick(s, "desc")}</p>
                  {features.length > 0 && (
                    <ul className="mt-6 space-y-2 text-sm text-navy/75">
                      {features.map((f, i) => (
                        <li key={i} className="flex gap-2"><span className="text-gold">◆</span>{f}</li>
                      ))}
                    </ul>
                  )}
                  {s.show_button ? (
                    (s.external_url || s.pdf_url) ? (
                      <div className="mt-8 flex flex-wrap gap-3" data-testid={`service-cta-${s.key}`}>
                        {s.external_url && (
                          <a
                            href={s.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`service-external-${s.key}`}
                            className="inline-flex items-center gap-2 px-5 py-3 bg-gold text-navy text-sm font-semibold rounded-sm hover:brightness-110"
                          >
                            {(lang === "ar" ? s.button_text_ar : s.button_text_en) || (lang === "ar" ? "اعرف المزيد" : "Learn more")}
                            <ExternalLink size={14} />
                          </a>
                        )}
                        {s.pdf_url && (
                          <a
                            href={s.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`service-pdf-${s.key}`}
                            className="inline-flex items-center gap-2 px-5 py-3 border border-navy/60 text-navy text-sm rounded-sm hover:bg-navy hover:text-cream"
                          >
                            <FileDown size={14} />
                            {lang === "ar" ? "تحميل PDF" : "Download PDF"}
                          </a>
                        )}
                      </div>
                    ) : (
                      <Link to="/quote" data-testid={`service-quote-${s.key}`} className="mt-8 inline-flex items-center gap-2 text-gold text-sm uppercase tracking-widest">
                        {t("nav.quote")} <ArrowRight size={14} />
                      </Link>
                    )
                  ) : null}
                </div>
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}

